import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import globalFeatureContent from "../globalFeatureContent.json";
import { normalizeLanguageCode } from "../supportedLanguages";
import {
  getMicrosoftStoreUrl,
  trackMicrosoftStoreClick,
} from "../utils/microsoftStore";
import styles from "./Download.module.css";

type GlobalFeatureContent = (typeof globalFeatureContent)["en"];

const globalFeatureContentByLanguage = globalFeatureContent as Record<
  string,
  GlobalFeatureContent
>;

const Download: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
  const normalizedLanguage = normalizeLanguageCode(language);
  const localizedGlobalFeatures =
    globalFeatureContentByLanguage[normalizedLanguage] ||
    globalFeatureContentByLanguage.en;
  const downloadUrl = getMicrosoftStoreUrl("download_page");
  const downloadImage = "/assets/images/DownloadBadgeSmall.png";
  const translatedGuidePoints = t("home_page.guide.points", {
    returnObjects: true,
  });
  const guidePoints = Array.isArray(translatedGuidePoints)
    ? translatedGuidePoints
    : [];

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.copy}>
          <p className={styles.eyebrow}>AUTOVID · Windows · Microsoft Store</p>
          <h1>AUTOVID {t("navigation.download")}</h1>
          <h2>{t("home_page.final.title")}</h2>
          <p>{t("home_page.final.lead1")}</p>
          <p>{t("home_page.final.lead2")}</p>

          <ul className={styles.highlights}>
            {guidePoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>

          <div className={styles.actions}>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackMicrosoftStoreClick("download_page")}
              className={styles.storeLink}
            >
              <img
                src={downloadImage}
                alt={t("home_page.final.primary_cta")}
                className={styles.downloadImage}
              />
            </a>

            <Link to={`/${language}/features`} className={styles.secondaryLink}>
              {t("home_page.final.secondary_cta2")}
            </Link>
          </div>
        </div>

        <aside className={styles.productCard} aria-label="AUTOVID">
          <img src="/assets/images/wide_logo.png" alt="AUTOVID" width={210} />
          <div className={styles.productMeta}>
            <span>Windows</span>
            <span>Microsoft Store</span>
            <span>{localizedGlobalFeatures.globalTitle}</span>
          </div>
          <Link to={`/${language}/sample-videos`} className={styles.sampleLink}>
            {t("home_page.final.secondary_cta1")}
          </Link>
        </aside>
      </section>
    </div>
  );
};

export default Download;
